import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { StrategyService } from '../../services/strategy.service';
import { Strategy } from '../../interfaces/models/strategy.model';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ConfirmPopup, ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { authUser } from '../../interfaces/auth/auth.model';
import { AuthService } from '../../services/auth.service';
import { TagModule } from 'primeng/tag';
import { TranslateModule } from '@ngx-translate/core';
import { DirectionService } from '../../services/direction.service';
import { Strategy404Component } from '../../shared/components/strategy404/strategy404.component';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { CustomAblePipe } from '../../pipes/able.pipe';
import { SafeStorageService } from '../../services/safe-storage.service';
import { NavTitlePipe } from '../../pipes/nav-title.pipe';

@Component({
  selector: 'app-strategy',
  standalone: true,
  imports: [
    NavTitlePipe,
    CustomAblePipe,
    TooltipModule,
    TableModule,
    ButtonModule,
    Strategy404Component,
    CommonModule,
    RouterModule,
    ConfirmPopupModule,
    ToastModule,
    TagModule,
    TranslateModule,
    FormsModule,
  ],
  templateUrl: './strategy.component.html',
  styleUrl: './strategy.component.scss',
  providers: [ConfirmationService, MessageService]
})
export class StrategyComponent implements OnInit, OnDestroy {
  @ViewChild(ConfirmPopup) confirmPopup!: ConfirmPopup;
  dir!: 'ltr' | 'rtl';
  displayType: 'cards' | 'list' = 'cards';
  strategies = [] as Strategy[];
  searchTerm = '';
  private unsubscribe$ = new Subject<void>();
  user = {} as authUser;
  openMenuId: number | null = null;

  constructor(
    private strategyService: StrategyService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private directionService: DirectionService,
    private safeStorage: SafeStorageService,
  ) {}

  get filteredStrategies(): Strategy[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.strategies;
    return this.strategies.filter((strategy) =>
      (strategy.name || '').toLowerCase().includes(term)
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
    this.authService.authBufferSub$.pipe(takeUntil(this.unsubscribe$)).subscribe((user) => {
      this.user = user as authUser;
    });

    this.route.data.pipe(takeUntil(this.unsubscribe$)).subscribe(({ strategies }) => {
      this.strategies = strategies;
    });

    this.directionService.direction$.pipe(takeUntil(this.unsubscribe$)).subscribe((dir) => {
      this.dir = dir;
    });
  }

  accept() {
    this.confirmPopup.accept();
  }

  reject() {
    this.confirmPopup.reject();
  }

  confirm(event: Event, id: number) {
    const target = event.target as HTMLElement;
    const isRtl = this.dir === 'rtl';
    this.confirmationService.confirm({
      target: target,
      message: isRtl ? 'هل تريد تأكيد المسح؟' : 'Do you want to confirm deletion?',
      accept: () => {
        this.strategyService.deleteStrategy(id).pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
          this.messageService.add({
            severity: 'success',
            summary: isRtl ? 'تأكيد' : 'Confirmed',
            detail: isRtl ? 'تم مسح الاستراتيجية' : 'Strategy deleted',
            life: 3000
          });
          this.strategies = this.strategies.filter((strategy) => strategy.id != id);
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'error',
          summary: isRtl ? 'الغاء' : 'Cancelled',
          detail: isRtl ? 'تم الالغاء' : 'Action cancelled',
          life: 3000
        });
      }
    });
  }

  toggleMenu(strategyId: number, event: Event) {
    event.stopPropagation();
    this.openMenuId = this.openMenuId === strategyId ? null : strategyId;
  }

  isMenuOpen(strategyId: number): boolean {
    return this.openMenuId === strategyId;
  }

  closeMenu() {
    this.openMenuId = null;
  }

  calculateProgress(strategy: Strategy): number {
    const startDate = new Date(strategy.start_date);
    const endDate = new Date(strategy.end_date);
    const currentDate = new Date();

    if (currentDate < startDate) return 0;
    if (currentDate > endDate) return 100;

    const totalDays = endDate.getTime() - startDate.getTime();
    if (totalDays <= 0) return 0;
    const daysPassed = currentDate.getTime() - startDate.getTime();

    return Math.round((daysPassed / totalDays) * 100);
  }

  getStrategyStatus(strategy: Strategy): 'active' | 'upcoming' | 'completed' {
    const currentDate = new Date();
    const startDate = new Date(strategy.start_date);
    const endDate = new Date(strategy.end_date);

    if (currentDate >= startDate && currentDate <= endDate) {
      return 'active';
    }
    if (currentDate < startDate) {
      return 'upcoming';
    }
    return 'completed';
  }

  getDefaultCount(): number {
    return this.strategies.filter((s) => s.is_default).length;
  }

  getStatusCount(status: 'active' | 'upcoming' | 'completed'): number {
    return this.strategies.filter((s) => this.getStrategyStatus(s) === status).length;
  }
}
